import type { LatestExecution } from './BrandWorkflows.types';

/* Every workflow type the orchestrator can report on an execution, with how it should read in the
   UI and whether the Manual Triggers tab may offer it.

   Mirrors WorkflowType in @proxima-workflows/orchestrator-api plus each workflow definition's
   allowManualTrigger — keep it in step when the orchestrator adds or retires a workflow.

   manuallyTriggerable is false for two reasons: a definition that sets allowManualTrigger: false
   (CreativeClassificationKickoff, which another workflow's success hook fires), or a deprecated
   type with no registered definition at all, where triggering fails with "No workflow definition
   registered for type". Either way the type still appears in execution history, so it needs a
   display name even though it must not be offered as a trigger.

   schemaName is the server-side Zod schema that validates the trigger payload (400 on mismatch);
   triggerable workflows without one accept any payload. */
type WorkflowCatalogEntry = {
  displayName: string;
  description: string;
  manuallyTriggerable: boolean;
  schemaName?: string;
};

const WORKFLOW_CATALOG: Record<string, WorkflowCatalogEntry> = {
  BrandOverviewGenerate: {
    displayName: 'Brand Overview',
    description: 'Generates the brand overview, testimonials, and purpose categories',
    manuallyTriggerable: true,
    schemaName: 'BrandOverviewGenerateTriggerPayloadSchema'
  },
  BrandTagGenerate: {
    displayName: 'Brand Tag Generate',
    description: 'Generates brand tags from the brand’s products and content',
    manuallyTriggerable: true
  },
  BrandTaggingPipeline: {
    displayName: 'Brand Tagging Pipeline',
    description: 'Runs brand tag generation and relevance scoring end to end',
    manuallyTriggerable: true,
    schemaName: 'BrandTaggingPipelineTriggerPayloadSchema'
  },
  BuyerSegmentPipeline: {
    displayName: 'Buyer Segment Pipeline',
    description: 'Builds buyer segments from the brand’s customer data',
    manuallyTriggerable: true
  },
  CastingProfileGenerate: {
    displayName: 'Casting Profile Generate',
    description: 'Deprecated — now a step inside Creative Engine Bootstrap',
    manuallyTriggerable: false
  },
  CreativeBriefGeneration: {
    displayName: 'Creative Brief Generation',
    description: 'Generates creative concepts, briefs, and creatives for the brand',
    manuallyTriggerable: true,
    schemaName: 'CreativeBriefGenerationTriggerPayloadSchema'
  },
  CreativeClassificationKickoff: {
    displayName: 'Creative Classification Kickoff',
    description: 'Classifies the brand’s Meta creatives',
    manuallyTriggerable: false
  },
  CreativeEngineBootstrap: {
    displayName: 'Creative Engine Bootstrap',
    description:
      'Bootstraps Creative Engine: product settings, casting profile, tags, and strategy',
    manuallyTriggerable: true
  },
  CreativeStrategySelect: {
    displayName: 'Creative Strategy Select',
    description: 'Deprecated — now a step inside Creative Engine Bootstrap',
    manuallyTriggerable: false
  },
  KlaviyoSync: {
    displayName: 'Klaviyo Sync',
    description: 'Syncs Klaviyo data for the brand (incremental or full)',
    manuallyTriggerable: true,
    schemaName: 'KlaviyoSyncTriggerPayloadSchema'
  },
  KlaviyoTransformLoadReprocess: {
    displayName: 'Klaviyo Transform Load Reprocess',
    description: 'Re-runs the Klaviyo transform and load over previously extracted data',
    manuallyTriggerable: true,
    schemaName: 'KlaviyoTransformLoadReprocessTriggerPayloadSchema'
  },
  BrandMetaAdStatisticsLoad: {
    displayName: 'Meta Ad Statistics',
    description: 'Loads aggregated Meta ad statistics for the brand',
    manuallyTriggerable: true
  },
  MetaAdsDataLoad: {
    displayName: 'Meta Ads Data Load',
    description: 'Loads Meta ads data, creative assets, and classification into the platform',
    manuallyTriggerable: true
  },
  MetaAdsSync: {
    displayName: 'Meta Ads Sync',
    description: 'Syncs Meta ads data for the brand (incremental or full)',
    manuallyTriggerable: true,
    schemaName: 'MetaAdsSyncTriggerPayloadSchema'
  },
  BrandShopifyBuyerStatisticsLoad: {
    displayName: 'Shopify Buyer Statistics',
    description: 'Loads aggregated Shopify buyer statistics for the brand',
    manuallyTriggerable: true
  },
  ShopifyDataLoad: {
    displayName: 'Shopify Data Load',
    description: 'Loads Shopify data and product aggregates into the platform',
    manuallyTriggerable: true
  },
  ShopifySync: {
    displayName: 'Shopify Sync',
    description: 'Syncs Shopify data for the brand (incremental or full)',
    manuallyTriggerable: true,
    schemaName: 'ShopifySyncTriggerPayloadSchema'
  },
  StyleGuideGenerate: {
    displayName: 'Style Guide Generate',
    description: 'Generates the brand’s style guide',
    manuallyTriggerable: true
  }
};

/* Readable name for a workflow type wherever one is shown to a user. Uncatalogued types keep
   their raw PascalCase — routing and the API always use the raw type, never this. */
export const workflowDisplayName = (workflowType: string): string =>
  WORKFLOW_CATALOG[workflowType]?.displayName ?? workflowType;

export type ManualTriggerEntry = {
  workflowType: string;
  displayName: string;
  description: string | null;
  schemaName: string | null;
  lastRun: LatestExecution;
};

export const getManualTriggerEntries = (rows: LatestExecution[]): ManualTriggerEntry[] =>
  rows
    /* An uncatalogued type is assumed triggerable so a workflow the orchestrator has just added
       still surfaces here — with its raw name and a disabled payload editor — rather than
       silently vanishing until this catalog catches up */
    .filter(row => WORKFLOW_CATALOG[row.workflowType]?.manuallyTriggerable ?? true)
    .map(row => {
      const catalogEntry = WORKFLOW_CATALOG[row.workflowType];
      return {
        workflowType: row.workflowType,
        displayName: catalogEntry?.displayName ?? row.workflowType,
        description: catalogEntry?.description ?? null,
        schemaName: catalogEntry?.schemaName ?? null,
        lastRun: row
      };
    });
